import { useTranslations } from 'next-intl';
import { useEvent, useUnit } from 'effector-react';
import { closeModalEvent, openModalEvent } from '@nx-frontend/shared/ui/modal';
import { useMenuItemVisibilityChanges } from '@nx-frontend/shared/hooks/use-changes';
import useOpenPaywallPopup from '@nx-frontend/shared/hooks/use-navigate/use-paywall-popup';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { getMenuItemByIdFx } from '@nx-frontend/shared/store/feature-menu-tree';
import { $orgMaxMembers } from '@nx-frontend/shared/store/org';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { $orgLimitsData } from '@nx-frontend/shared/store/org-limits';
import { $portalSettings } from '@nx-frontend/shared/store/portal';
import {
  ManageMembersType,
  ManageMembersView,
  MenuType,
  OrgInfoSubscriptionType,
  PaywallPopupReason,
  PopupType,
} from '@nx-frontend/shared/types';
import { getNodeNameByType } from '@nx-frontend/shared/utils';

export interface OpenManageAccessPayload {
  id: string;
  menuType: MenuType;
  resourceUrl?: string;
  reason?: PaywallPopupReason;
}

export const useOpenManageAccessPopup = () => {
  const t = useTranslations('customizer');
  const [currentWorkspace, currentOrganization, orgMaxMembers, portalSettings, orgInfo, orgLimits] = useUnit([
    $currentWorkspace,
    $currentOrganization,
    $orgMaxMembers,
    $portalSettings,
    $orgInfo,
    $orgLimitsData,
  ]);
  const portalId = portalSettings.id;
  const portalDomain = portalSettings.domain;
  const [openModal, closeModal, getMenuItemById] = useEvent([openModalEvent, closeModalEvent, getMenuItemByIdFx]);
  const openPaywallPopup = useOpenPaywallPopup();
  const setMenuItemVisibilityChanges = useMenuItemVisibilityChanges();

  return async ({ id, menuType, resourceUrl, reason }: OpenManageAccessPayload) => {
    const menuItem = await getMenuItemById({ menuType, id });
    const isVisible = menuItem.visible;

    if ([OrgInfoSubscriptionType.PLUS, OrgInfoSubscriptionType.STANDARD].includes(orgInfo.subscriptionType)) {
      openPaywallPopup({
        reason: reason || PaywallPopupReason.PORTAL_MANAGE_ACCESS_GRANULAR,
      });
      return;
    }
    const nodeName = getNodeNameByType(menuItem.type);
    const modalTitle = t('customizer-unpublished_item_permissions_title', { nodeName: nodeName });

    if (menuItem.isDraft) {
      openModal({
        content: PopupType.PUBLISH_INFORM,
        showCloseButton: true,
        title: modalTitle,
        closeButtonSize: 18,
        payload: {
          payloadType: PopupType.PUBLISH_INFORM,
          data: {
            isVisible: menuItem.visible,
            nodeName,
            onSubmit: () => {
              if (isVisible) {
                setMenuItemVisibilityChanges({ id: menuItem.id, menuType, visible: false });
              }
              closeModal();
              openModal({ position: { top: '51.5px', right: '16px' } });
            },
          },
        },
      });
    } else {
      openModal({
        content: PopupType.MANAGE_MEMBERS,
        showCloseButton: false,
        payload: {
          payloadType: PopupType.MANAGE_MEMBERS,
          data: {
            type: ManageMembersType.PORTAL_NODE,
            initialViewMode: ManageMembersView.NODE_ACCESS,
            orgMaxMembers,
            context: {
              workspaceId: currentWorkspace.globalId,
              orgId: currentOrganization.globalId,
              portalId,
              portalUrl: resourceUrl || portalDomain,
            },
            orgLimits: {
              membersPerOrg: orgLimits.membersPerOrg,
              guests: orgLimits.guests,
            },
            menuItem,
            onClose: () => {
              closeModal();
            },
          },
        },
      });
    }
  };
};
